import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  TrendingUp,
  ShoppingCart,
  ShoppingBag,
  Receipt,
  Users,
  Wallet,
  Store as StoreIcon,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";
import { fetchInsights } from "../../../redux/apis/sales-drill-down-api";
import { fmtINR } from "../utils";

// "Totals & Insights" panel above the breadcrumb. Holds six KPI tiles
// + a daily sales-vs-purchase bar chart + a top-stores horizontal bar
// list. Same scope as the level grid (constraints + date range), and
// collapses to a single header strip when the user hides it.
//
// Colors come from the global --dashboard-* tokens so the panel sits
// inside the rest of the CPOS app's visual language without one-off
// hex values.

export default function InsightsPanel({ constraints, from, to, onDrillSales, onDrillPurchase }) {
  const [collapsed, setCollapsed] = useState(false);
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchInsights({ constraints, from, to })
      .then((data) => {
        if (cancelled) return;
        setState({ loading: false, error: null, data });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ loading: false, error: err, data: null });
      });
    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(constraints), from, to]);

  const k = state.data?.kpis;
  const trend = state.data?.trend || [];
  const topStores = state.data?.top_stores || [];

  return (
    <section
      className="rounded-xl border border-[var(--dashboard-gray-200)] bg-white shadow-sm overflow-hidden"
      aria-label="Totals & Insights"
    >
      <PanelHeader collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
      {!collapsed && (
        <div className="px-4 pb-4 pt-2">
          {state.loading ? (
            <PanelLoading />
          ) : state.error ? (
            <PanelError msg={state.error?.message || "Couldn't load insights"} />
          ) : !state.data ? null : (
            <>
              <KpiRow kpis={k} onDrillSales={onDrillSales} onDrillPurchase={onDrillPurchase} />
              <div className="mt-4 grid grid-cols-1 xl:grid-cols-[1.65fr_1fr] gap-4">
                <TrendCard trend={trend} />
                <TopStoresCard rows={topStores} />
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

/* ----------------------------- panel header ---------------------------- */
function PanelHeader({ collapsed, onToggle }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--dashboard-gray-100)] bg-white">
      <div className="flex items-baseline gap-2">
        <h2 className="text-[14px] font-bold text-[var(--dashboard-gray-800)] tracking-tight">
          Totals &amp; Insights
        </h2>
        <span className="text-[12px] text-[var(--dashboard-gray-500)]">
          key figures &amp; charts
        </span>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[var(--dashboard-gray-600)] bg-white border border-[var(--dashboard-gray-200)] rounded-md px-3 py-1.5 hover:border-[var(--dashboard-gray-300)] hover:text-[var(--dashboard-gray-900)]"
      >
        {collapsed ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
        {collapsed ? "Show totals & charts" : "Hide totals & charts"}
      </button>
    </div>
  );
}

/* --------------------------------- KPI row ----------------------------- */
function KpiRow({ kpis, onDrillSales, onDrillPurchase }) {
  if (!kpis) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
      <Kpi
        accent="sales"
        Icon={ShoppingCart}
        label="Overall sales amount"
        value={fmtINR(kpis.sales)}
        sub={`${kpis.sales_count} sales invoice${kpis.sales_count === 1 ? "" : "s"}`}
        hint="click to drill in ›"
        onClick={onDrillSales}
      />
      <Kpi
        accent="purchase"
        Icon={ShoppingBag}
        label="Overall purchase"
        value={fmtINR(kpis.purchase)}
        sub={`${kpis.purchase_count} GRN${kpis.purchase_count === 1 ? "" : "s"}`}
        onClick={onDrillPurchase}
      />
      <Kpi
        accent="neutral"
        Icon={TrendingUp}
        label="Gross margin"
        value={fmtINR(kpis.gross_margin)}
        sub={`${kpis.gross_margin_pct}% of sales`}
      />
      <Kpi
        accent="neutral"
        Icon={Receipt}
        label="Sales invoices"
        value={String(kpis.sales_count)}
        sub={`${kpis.unique_customers} unique customer${kpis.unique_customers === 1 ? "" : "s"}`}
      />
      <Kpi
        accent="neutral"
        Icon={Wallet}
        label="Avg invoice value"
        value={fmtINR(kpis.avg_invoice_value)}
        sub="per sales bill"
      />
      <Kpi
        accent="neutral"
        Icon={StoreIcon}
        label="Active stores"
        value={String(kpis.active_stores)}
        sub={`of ${kpis.total_stores} stores`}
      />
    </div>
  );
}

function Kpi({ accent, Icon, label, value, sub, hint, onClick }) {
  const tone = ACCENT_MAP[accent] || ACCENT_MAP.neutral;
  const interactive = typeof onClick === "function";
  const Tag = interactive ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      type={interactive ? "button" : undefined}
      className={[
        "text-left rounded-lg border px-4 py-3 transition",
        tone.bg,
        tone.border,
        interactive ? "hover:shadow-sm hover:-translate-y-px cursor-pointer" : "cursor-default",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`text-[10.5px] font-bold uppercase tracking-wide ${tone.label}`}>
          {label}
        </div>
        {hint ? (
          <span className={`text-[10.5px] ${tone.hint}`}>{hint}</span>
        ) : (
          <Icon size={14} className={tone.icon} />
        )}
      </div>
      <div className={`font-mono text-[20px] font-bold mt-1.5 leading-tight ${tone.value}`}>
        {value}
      </div>
      <div className={`text-[11.5px] mt-1 ${tone.sub}`}>{sub}</div>
    </Tag>
  );
}

// Sales / purchase tiles match the drill-down MetricPill styling — same
// tinted fill, same dark-on-tint text — so the insights row reads as the
// same visual language as the cards below. Neutral tiles stay white so
// they don't compete with the colored ones.
const ACCENT_MAP = {
  sales: {
    bg: "bg-[var(--dashboard-bg-success)]",
    border: "border-[var(--dashboard-bg-success)]",
    label: "text-[var(--dashboard-green-dark)]",
    icon: "text-[var(--dashboard-green-dark)]",
    value: "text-[var(--dashboard-green-dark)]",
    sub: "text-[var(--dashboard-green-dark)]/75",
    hint: "text-[var(--dashboard-green-dark)]/75",
  },
  purchase: {
    bg: "bg-[var(--dashboard-bg-warning)]",
    border: "border-[var(--dashboard-bg-warning)]",
    label: "text-[var(--dashboard-warning-dark)]",
    icon: "text-[var(--dashboard-warning-dark)]",
    value: "text-[var(--dashboard-warning-dark)]",
    sub: "text-[var(--dashboard-warning-dark)]/75",
    hint: "text-[var(--dashboard-warning-dark)]/75",
  },
  neutral: {
    bg: "bg-white",
    border: "border-[var(--dashboard-gray-200)]",
    label: "text-[var(--dashboard-gray-500)]",
    icon: "text-[var(--dashboard-gray-400)]",
    value: "text-[var(--dashboard-gray-900)]",
    sub: "text-[var(--dashboard-gray-500)]",
    hint: "text-[var(--dashboard-gray-400)]",
  },
};

/* ----------------------------- Trend chart ----------------------------- */
function TrendCard({ trend }) {
  // Format the day-bucket date strings into short "1 Jun" labels.
  const data = useMemo(
    () =>
      (trend || []).map((d) => ({
        ...d,
        dayLabel: formatDay(d.date),
      })),
    [trend]
  );
  const hasAny = data.some((d) => d.sales > 0 || d.purchase > 0);

  return (
    <div className="rounded-lg border border-[var(--dashboard-gray-200)] bg-white p-4">
      <h3 className="text-[13.5px] font-bold text-[var(--dashboard-gray-800)] mb-2">
        Sales vs Purchase trend
      </h3>
      {hasAny ? (
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 0" vertical={false} stroke="var(--dashboard-gray-200)" />
              <XAxis
                dataKey="dayLabel"
                tick={{ fill: "var(--dashboard-gray-500)", fontSize: 11 }}
                axisLine={{ stroke: "var(--dashboard-gray-200)" }}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fill: "var(--dashboard-gray-400)", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => fmtCompact(v)}
              />
              <Tooltip
                cursor={{ fill: "var(--dashboard-bg-hover)" }}
                contentStyle={{
                  background: "#fff",
                  border: "1px solid var(--dashboard-gray-200)",
                  borderRadius: 8,
                  fontSize: 12,
                  padding: "8px 10px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
                labelStyle={{ color: "var(--dashboard-gray-700)", fontWeight: 600 }}
                formatter={(v, name) => [fmtINR(v), name]}
              />
              <Legend
                wrapperStyle={{ paddingTop: 4, fontSize: 12, color: "var(--dashboard-gray-600)" }}
                iconType="circle"
                iconSize={8}
              />
              <Bar
                dataKey="sales"
                name="Sales"
                fill="var(--dashboard-success)"
                radius={[2, 2, 0, 0]}
                maxBarSize={18}
              />
              <Bar
                dataKey="purchase"
                name="Purchase"
                fill="var(--dashboard-warning)"
                radius={[2, 2, 0, 0]}
                maxBarSize={18}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyChart msg="No transactions in this period." />
      )}
    </div>
  );
}

/* --------------------------- Top stores list -------------------------- */
function TopStoresCard({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="rounded-lg border border-[var(--dashboard-gray-200)] bg-white p-4">
        <h3 className="text-[13.5px] font-bold text-[var(--dashboard-gray-800)] mb-3">
          Top stores by sales
        </h3>
        <EmptyChart msg="No store sales in scope." />
      </div>
    );
  }
  // Bar widths normalise against the leader so the top bar is full-width
  // and the rest scale relative to it.
  const max = Math.max(...rows.map((r) => r.sales || 0)) || 1;
  return (
    <div className="rounded-lg border border-[var(--dashboard-gray-200)] bg-white p-4">
      <h3 className="text-[13.5px] font-bold text-[var(--dashboard-gray-800)] mb-3">
        Top stores by sales
      </h3>
      <ul className="space-y-2.5">
        {rows.map((r) => {
          const pct = Math.round((r.sales / max) * 100);
          return (
            <li key={r.store_id} className="grid grid-cols-[160px_1fr_auto] gap-3 items-center">
              <div>
                <div className="font-mono text-[12.5px] font-bold text-[var(--dashboard-gray-900)]">
                  {r.store_code || r.store_id}
                </div>
                <div className="text-[11px] text-[var(--dashboard-gray-500)]">{r.city || "—"}</div>
              </div>
              <div className="h-2 rounded-full bg-[var(--dashboard-gray-100)] overflow-hidden">
                <div
                  className="h-full rounded-full bg-[var(--dashboard-success)]"
                  style={{ width: `${pct}%`, transition: "width 240ms ease-out" }}
                />
              </div>
              <div className="text-right whitespace-nowrap">
                <div className="font-mono text-[12.5px] font-bold text-[var(--dashboard-gray-900)]">
                  {fmtINR(r.sales)}
                </div>
                <div className="text-[11px] text-[var(--dashboard-gray-500)]">{r.pct}%</div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* --------------------------- helpers + states ------------------------- */
function PanelLoading() {
  return (
    <div className="py-10 flex items-center justify-center text-[var(--dashboard-gray-500)]">
      <Loader2 size={20} className="animate-spin text-[var(--dashboard-green-dark)]" />
      <span className="ml-2 text-[13px]">Loading totals…</span>
    </div>
  );
}
function PanelError({ msg }) {
  return (
    <div className="py-6 px-3 text-[13px] text-[var(--dashboard-danger)] bg-[#FEF2F2] rounded-md">
      {msg}
    </div>
  );
}
function EmptyChart({ msg }) {
  return (
    <div className="py-10 text-center text-[var(--dashboard-gray-500)] text-[12.5px]">{msg}</div>
  );
}
function fmtCompact(n) {
  const v = Math.round(Number(n) || 0);
  if (v >= 10000000) return (v / 10000000).toFixed(1) + "Cr";
  if (v >= 100000) return (v / 100000).toFixed(1) + "L";
  if (v >= 1000) return (v / 1000).toFixed(0) + "k";
  return String(v);
}
function formatDay(d) {
  if (!d) return "";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (!(dt instanceof Date) || isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}
